// src/components/AdminPortal.tsx
// ✅ MOBILE-RESPONSIVE VERSION WITH FEEDBACK

import { useNavigate, useLocation } from 'react-router-dom';
import { Authenticator, Theme, ThemeProvider } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { useState, useEffect } from 'react';
import { ZodiyakBubble } from './ZodiyakBubble';

import PendingVerifications from './admin/PendingVerifications';
import MemeManager from './admin/MemeManager';
import MemeSubmissionsModerator from './admin/MemeSubmissionsModerator';
import FeedbackManager from './admin/FeedbackManager';

type AdminTab = 'verifications' | 'memes' | 'submissions' | 'feedback' | 'analytics';
type SignOutFn = ((...args: any[]) => void) | undefined;

const ZODIYAK_GREETINGS = [
  "Hey there, matchmaker! Ready to spread some love? 💕",
  "Welcome back! The universe is aligning... ✨",
  "Someone's gotta keep the crushes flowing! 🌟",
  "Admin mode: activated. Cupid would be proud! 💘",
  "Time to make some magic happen! 🔮",
  "Let's get this love train rolling! 🚂💕",
];

const ZODIYAK_SIGNS = [
  "Aquarius", "Pisces", "Aries", "Taurus", "Gemini", "Cancer",
  "Leo", "Virgo", "Libra", "Scorpio", "Sagittarius", "Capricorn"
];

const crushhhTheme: Theme = {
  name: 'crushhh-admin-theme',
  tokens: {
    colors: {
      brand: {
        primary: {
          10: '#fff5f5',
          20: '#ffe5e5',
          40: '#ffc9c9',
          60: '#ff9d9d',
          80: '#f46d6d',
          90: '#e54c4c',
          100: '#d43939',
        },
      },
      background: {
        primary: 'rgba(255, 255, 255, 0.95)',
        secondary: '#fef5ed',
      },
    },
    components: {
      authenticator: {
        router: {
          backgroundColor: 'transparent',
          borderWidth: '0',
          boxShadow: '0 20px 50px rgba(0, 0, 0, 0.1)',
        },
      },
      tabs: {
        item: {
          color: '{colors.neutral.80}',
          borderColor: 'transparent',
          _hover: {
            color: '{colors.brand.primary.80}',
          },
          _active: {
            color: '{colors.brand.primary.80}',
            borderColor: '{colors.brand.primary.80}',
          },
        },
      },
      button: {
        primary: {
          backgroundColor: '{colors.brand.primary.80}',
          color: '{colors.white}',
          _hover: {
            backgroundColor: '{colors.brand.primary.90}',
          },
          _active: {
            backgroundColor: '{colors.brand.primary.100}',
          },
        },
      },
      fieldcontrol: {
        borderColor: '{colors.neutral.40}',
        _focus: {
          borderColor: '{colors.brand.primary.80}',
        },
      },
    },
  },
};


function AdminPortalContent({ signOut }: { signOut: SignOutFn }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showGreeting, setShowGreeting] = useState(true);
  const [greeting] = useState(() => 
    ZODIYAK_GREETINGS[Math.floor(Math.random() * ZODIYAK_GREETINGS.length)]
  );
  const [zodiyakSign] = useState(() => 
    ZODIYAK_SIGNS[Math.floor(Math.random() * ZODIYAK_SIGNS.length)]
  );

  const activeTab: AdminTab = (() => {
    const path = location.pathname.replace('/admin', '').replace('/', '') || 'verifications';
    if (['verifications', 'memes', 'submissions', 'feedback', 'analytics'].includes(path)) {
      return path as AdminTab;
    }
    return 'verifications';
  })();

  const setActiveTab = (tab: AdminTab) => {
    navigate(`/admin/${tab}`);
  };

  useEffect(() => {
    const timer = setTimeout(() => setShowGreeting(false), 6000);
    return () => clearTimeout(timer);
  }, []);

  // Widen #root while admin is mounted so dense tables aren't squeezed to 480px.
  // Matches the crushhh-camera-open body-class pattern used in ZodiacPhotoFrame.
  useEffect(() => {
    document.body.classList.add('admin-mode');
    return () => document.body.classList.remove('admin-mode');
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
      {showGreeting && (
        <div className="fixed bottom-4 left-4 md:bottom-6 md:left-6 z-50 flex items-end gap-2 md:gap-3 animate-bounce-in">
          <img
            src={`/SVG/zodiyak-${zodiyakSign.toLowerCase()}.svg`}
            alt="Admin Zodiyak"
            className="w-16 h-16 md:w-24 md:h-24 object-contain drop-shadow-2xl hover:scale-110 transition-transform cursor-pointer"
            onClick={() => setShowGreeting(false)}
          />
          <div className="relative max-w-[200px] md:max-w-none">
            <ZodiyakBubble>
              <p className="text-sm md:text-base font-medium">{greeting}</p>
            </ZodiyakBubble>
            <button
              onClick={() => setShowGreeting(false)}
              className="absolute -top-2 -right-2 w-6 h-6 bg-brand text-white rounded-full text-xs hover:bg-branddark transition-colors shadow-lg flex items-center justify-center font-bold"
              aria-label="Close greeting"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      <header className="bg-white/90 backdrop-blur-md border-b-2 border-brand/20 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center py-3 md:py-5 gap-3 sm:gap-0">
            <div className="text-center sm:text-left">
              <h1 className="text-xl md:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-brand via-branddark to-brand animate-gradient">
                CRUSHHH Admin ✨
              </h1>
              <p className="text-xs text-gray-500 mt-1 font-medium">
                Where the magic happens 🪄
              </p>
            </div>

            <div className="flex items-center gap-2 md:gap-3">
              <button
                onClick={() => navigate('/')}
                className="px-3 md:px-4 py-1.5 md:py-2 text-xs md:text-sm text-gray-600 hover:text-brand transition-colors font-semibold rounded-lg hover:bg-brand/5"
              >
                ← Back
              </button>

              <button
                onClick={() => signOut?.()}
                className="px-3 md:px-5 py-1.5 md:py-2.5 text-xs md:text-sm font-bold text-white bg-gradient-to-r from-brand to-branddark rounded-lg hover:shadow-xl hover:scale-105 transition-all active:scale-95"
                type="button"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="bg-white/70 backdrop-blur-sm border-b border-brand/10">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
          <nav className="flex space-x-1 overflow-x-auto scrollbar-hide -mx-3 px-3 sm:mx-0 sm:px-0">
            <TabButton active={activeTab === 'verifications'} onClick={() => setActiveTab('verifications')} icon="✅" label="Verify" />
            <TabButton active={activeTab === 'memes'} onClick={() => setActiveTab('memes')} icon="🎨" label="Memes" />
            <TabButton active={activeTab === 'submissions'} onClick={() => setActiveTab('submissions')} icon="🏆" label="Contest" />
            <TabButton active={activeTab === 'feedback'} onClick={() => setActiveTab('feedback')} icon="💭" label="Feedback" />
            <TabButton active={activeTab === 'analytics'} onClick={() => setActiveTab('analytics')} icon="📊" label="Analytics" />
          </nav>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 md:py-8">
        <div className="animate-fadeIn">
          {activeTab === 'verifications' && <PendingVerifications />}
          {activeTab === 'memes' && <MemeManager />}
          {activeTab === 'submissions' && <MemeSubmissionsModerator />}
          {activeTab === 'feedback' && <FeedbackManager />}
          {activeTab === 'analytics' && (
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl md:rounded-3xl border-2 border-brand/20 p-8 md:p-16 text-center shadow-lg">
              <div className="text-5xl md:text-7xl mb-4 md:mb-6 animate-pulse">📊</div>
              <h3 className="text-2xl md:text-3xl font-black text-gray-800 mb-2 md:mb-3">Analytics Dashboard</h3>
              <p className="text-base md:text-lg text-gray-600 font-medium">
                Coming soon... We're cooking up something special! 🔥
              </p>
              <div className="mt-4 md:mt-6 inline-block px-3 md:px-4 py-1.5 md:py-2 bg-brand/10 text-brand rounded-full text-xs md:text-sm font-bold">
                Stay tuned ✨
              </div>
            </div>
          )}
        </div>
      </main>

      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        <div className="hidden md:block absolute top-20 left-10 text-4xl opacity-10 animate-float">💕</div>
        <div className="absolute top-40 right-10 md:right-20 text-2xl md:text-3xl opacity-10 animate-float-delayed">✨</div>
        <div className="hidden md:block absolute bottom-32 left-1/4 text-5xl opacity-10 animate-float">💕</div>
        <div className="absolute top-1/3 right-1/4 text-3xl md:text-4xl opacity-10 animate-float-delayed">💖</div>
      </div>
    </div>
  );
}

function TabButton({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: string; label: string; }) {
  return (
    <button 
      onClick={onClick} 
      className={`
        group relative py-3 md:py-4 px-3 md:px-4 font-bold text-xs md:text-sm transition-all whitespace-nowrap flex-shrink-0
        ${active ? 'text-brand' : 'text-gray-500 hover:text-brand/70'}
      `} 
      type="button"
    >
      <div className="flex items-center gap-1.5 md:gap-2">
        <span className={`text-base md:text-lg transition-transform ${active ? 'scale-125' : 'group-hover:scale-110'}`}>
          {icon}
        </span>
        <span className="hidden sm:inline">{label}</span>
      </div>
      
      <div className={`
        absolute bottom-0 left-0 right-0 h-1 rounded-t-full transition-all duration-300 ease-out
        ${active ? 'bg-gradient-to-r from-brand via-branddark to-brand' : 'bg-transparent group-hover:bg-brand/30'}
      `} />
      
      {active && (
        <div className="absolute inset-0 bg-brand/5 rounded-lg -z-10" />
      )}
    </button>
  );
}



export default function AdminPortal() {
  return (
    <ThemeProvider theme={crushhhTheme}>
      <Authenticator>
        {({ signOut }) => <AdminPortalContent signOut={signOut} />}
      </Authenticator>
    </ThemeProvider>
  );
}